/**
 * Regiões do mapa corporal: o ponto de referência de cada região no quadro da
 * silhueta (`BODY_SILHOUETTE_VIEWBOX`, 358 x 960), por vista, e a zona que
 * cada ponto clicado cai (`regionAtPoint`). Serve para sugerir a região do
 * clique e para marcar pela lista, sem clique.
 *
 * Lados são do paciente: de frente, o lado direito fica à esquerda de quem
 * olha; de costas, à direita. As chaves podem se repetir entre as vistas
 * (`shoulder_right` existe de frente e de costas): o marcador guarda a vista.
 */

export const BODY_VIEWS = Object.freeze({ FRONT: 'front', BACK: 'back' });

export const BODY_VIEW_OPTIONS = Object.freeze([
  { key: BODY_VIEWS.FRONT, label: 'Frente' },
  { key: BODY_VIEWS.BACK, label: 'Costas' },
]);

const CENTER_X = 179;
const mirror = (x) => 2 * CENTER_X - x;

const region = (view, key, label, x, y, side = null) => ({ view, key, label, x, y, side });

// Par direito/esquerdo: `xRight` é o x do lado direito do paciente visto de frente
const pair = (view, baseKey, [rightLabel, leftLabel], xRight, y) => {
  const rightX = view === BODY_VIEWS.BACK ? mirror(xRight) : xRight;
  return [
    region(view, `${baseKey}_right`, rightLabel, rightX, y, 'right'),
    region(view, `${baseKey}_left`, leftLabel, mirror(rightX), y, 'left'),
  ];
};

const F = BODY_VIEWS.FRONT;
const B = BODY_VIEWS.BACK;

export const BODY_REGIONS = Object.freeze([
  region(F, 'head', 'Cabeça', 179, 79),
  region(F, 'neck', 'Pescoço', 179, 150),
  ...pair(F, 'shoulder', ['Ombro direito', 'Ombro esquerdo'], 79, 191),
  region(F, 'chest', 'Tórax', 179, 238),
  ...pair(F, 'arm', ['Braço direito', 'Braço esquerdo'], 71, 317),
  ...pair(F, 'elbow', ['Cotovelo direito', 'Cotovelo esquerdo'], 59, 396),
  region(F, 'abdomen', 'Abdômen', 179, 402),
  ...pair(F, 'forearm', ['Antebraço direito', 'Antebraço esquerdo'], 39, 462),
  region(F, 'pelvis', 'Quadril / pelve', 179, 468),
  ...pair(F, 'hand', ['Mão direita', 'Mão esquerda'], 35, 534),
  ...pair(F, 'thigh', ['Coxa direita', 'Coxa esquerda'], 128, 567),
  ...pair(F, 'knee', ['Joelho direito', 'Joelho esquerdo'], 125, 660),
  ...pair(F, 'shin', ['Perna direita', 'Perna esquerda'], 119, 732),
  ...pair(F, 'foot', ['Pé direito', 'Pé esquerdo'], 125, 910),

  region(B, 'head', 'Cabeça', 179, 79),
  region(B, 'nape', 'Nuca', 179, 150),
  ...pair(B, 'shoulder', ['Ombro direito', 'Ombro esquerdo'], 79, 191),
  region(B, 'upper_back', 'Costas (parte alta)', 179, 225),
  ...pair(B, 'scapula', ['Escápula direita', 'Escápula esquerda'], 128, 255),
  ...pair(B, 'arm', ['Braço direito', 'Braço esquerdo'], 71, 317),
  region(B, 'lower_back', 'Lombar', 179, 385),
  ...pair(B, 'elbow', ['Cotovelo direito', 'Cotovelo esquerdo'], 59, 396),
  ...pair(B, 'forearm', ['Antebraço direito', 'Antebraço esquerdo'], 39, 462),
  ...pair(B, 'glute', ['Glúteo direito', 'Glúteo esquerdo'], 138, 475),
  ...pair(B, 'hand', ['Mão direita', 'Mão esquerda'], 35, 534),
  ...pair(B, 'thigh', ['Coxa direita (posterior)', 'Coxa esquerda (posterior)'], 128, 567),
  ...pair(B, 'knee', ['Joelho direito (posterior)', 'Joelho esquerdo (posterior)'], 125, 660),
  ...pair(B, 'calf', ['Panturrilha direita', 'Panturrilha esquerda'], 119, 732),
  ...pair(B, 'heel', ['Calcanhar direito', 'Calcanhar esquerdo'], 125, 915),
]);

export const FREE_POINT_LABEL = 'Ponto livre';

export function getRegionsForView(view) {
  return BODY_REGIONS.filter((item) => item.view === view);
}

export function findRegion(view, key) {
  if (!key) return null;
  return BODY_REGIONS.find((item) => item.view === view && item.key === key) || null;
}

/** Rótulo da região de um marcador; sem região (ou região desconhecida) é um ponto livre. */
export function regionLabel(view, key) {
  const found = findRegion(view, key);
  return found ? found.label : FREE_POINT_LABEL;
}

// Meia largura do tronco entre os braços, medida nas quatro silhuetas
const torsoHalfWidth = (y) => (y < 400 ? 78 : 95);

/**
 * Região sob um ponto do quadro (unidades do quadro, 358 x 960), por faixas
 * de altura e pela largura do corpo: dentro do tronco é tórax, abdômen ou
 * quadril; fora, ombro, braço ou mão; abaixo do quadril, a perna do lado.
 */
export function regionAtPoint(view, x, y) {
  const back = view === BODY_VIEWS.BACK;
  const offset = Math.abs(x - CENTER_X);
  const side = (x < CENTER_X) !== back ? 'right' : 'left';

  const key = (() => {
    if (y < 125) return 'head';
    if (y < 168) return back ? 'nape' : 'neck';
    if (y < 300) {
      if (offset > 72) return y < 225 ? `shoulder_${side}` : `arm_${side}`;
      if (back && y >= 215 && offset >= 25) return `scapula_${side}`;
      return back ? 'upper_back' : 'chest';
    }
    if (y < 510) {
      if (offset <= torsoHalfWidth(y)) {
        if (y < 440) return back ? 'lower_back' : 'abdomen';
        return back ? `glute_${side}` : 'pelvis';
      }
      if (y < 365) return `arm_${side}`;
      if (y < 425) return `elbow_${side}`;
      if (y < 495) return `forearm_${side}`;
      return `hand_${side}`;
    }
    if (y < 580 && offset > 100) return `hand_${side}`;
    if (y < 620) return `thigh_${side}`;
    if (y < 700) return `knee_${side}`;
    if (y < 860) return back ? `calf_${side}` : `shin_${side}`;
    return back ? `heel_${side}` : `foot_${side}`;
  })();

  return findRegion(view, key);
}
